import { isFeature } from "@/account/account-helpers";
import type {
  IOnboardingConfig,
  IProviderConfig,
  IUserInfo,
} from "@/account/account-models";
import { EFeature, EGenderType, EUserType } from "@/account/account-models";
import { getMaxAspectHeight } from "@/common/image-loader/image-loader-helpers";
import type { IInputConfig } from "@/common/input";
import { API_ROOT, FROM_DESKTOP, FROM_TABLET, FROM_WIDE } from "@/globals";
import type { IDictionary, IMediaType } from "@/models";
import { getLocalStorageValue } from "@/utils/localStorage";
import { EUnit, timeDiff } from "@/utils/time";
import { defined } from "@/utils/variable-evaluation";

const SUBSEQUENT_AMOUNT = 20;

export interface IGetProfilesConfig {
  isInitialRequest: boolean; // any request that doesn't use 'getNextProfiles' is true,
  // isDoubleRequest?: boolean; // two requests performed in sequence, one just to fill white-space, second for space below white space
  offset?: number;
  viewportWidth?: number; // required if isInitialRequest
  onboardingCode?: string;
  filtersSet: IDictionary<IDiscoverFilter>;
  isForceLoader?: boolean; // this is to force showing the loader (used in pullToRefresh)
}

export interface IDiscoverFilter {
  type?: EDiscoverFilterType;
  // TODO: shape depends on `type`, same as IChatFilter.value
  // (src/chats/chats-helpers.ts) - needs a discriminated union, not a
  // quick fix.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- This heterogeneous discover payload needs a discriminated card/result model.
  value?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- This heterogeneous discover payload needs a discriminated card/result model.
  defaultValue?: any;
  inputs?: IInputConfig[];
}

export function onboardingCode(onboardingConfig?: IOnboardingConfig | null) {
  return onboardingConfig?.init?.code_used;
}

/* Async */

// export function isModelSelected(prevProps, props) {
//   return (
//     prevProps.match.params.profileId !== props.match.params.profileId &&
//     defined(props.match.params.profileId)
//   );
// }

/* Viewport Size */

export function numberOfColumns(width: number) {
  if (width < FROM_TABLET) {
    return 2;
  }
  if (width < FROM_DESKTOP) {
    return 3;
  }
  if (width < FROM_WIDE) {
    return 4;
  }
  return 5;
}

/* Masonry columns */

// What a list card adds below its image: the name strip and the card border.
const CARD_CAPTION_PX = 30;
const CARD_BORDER_PX = 4;
// What the image loader assumes for media that has no dimensions of its own.
const FALLBACK_MEDIA_WIDTH = 400;
const FALLBACK_MEDIA_HEIGHT = 500;

/**
 * How tall a list card will be, from its image's dimensions, the way the card
 * itself sizes the image: the aspect ratio is clamped, and the image is capped
 * at `maxImageHeight`.
 */
export function estimateCardHeight(
  media: IMediaType | undefined,
  columnWidth: number,
  maxImageHeight: number
) {
  const imageHeight = getMaxAspectHeight(
    media?.width || FALLBACK_MEDIA_WIDTH,
    media?.height || FALLBACK_MEDIA_HEIGHT,
    columnWidth
  );
  const sized =
    typeof imageHeight === "number" ? imageHeight : columnWidth * 1.25;
  return Math.min(sized, maxImageHeight) + CARD_CAPTION_PX + CARD_BORDER_PX;
}

/**
 * Splits profiles into masonry columns: each next profile goes into the
 * column that is currently shortest (the leftmost one on a tie), so columns
 * stay level however much the card heights differ. Dealing profiles out by
 * index instead lets differences pile up and leaves some columns far shorter.
 *
 * A profile's column depends only on the profiles before it, so loading more
 * profiles never moves the ones already on screen.
 */
export function distributeIntoColumns<T extends { discover?: IMediaType }>(
  profiles: T[],
  numberOfColumns: number,
  columnWidth: number,
  maxImageHeight: number
): { profile: T; globalIndex: number }[][] {
  const columnCount = Math.max(1, Math.floor(numberOfColumns));
  const columns: { profile: T; globalIndex: number }[][] = Array.from(
    { length: columnCount },
    () => []
  );
  const heights: number[] = Array.from({ length: columnCount }, () => 0);

  profiles.forEach((profile, globalIndex) => {
    let shortest = 0;
    for (let column = 1; column < columnCount; column++) {
      if (heights[column] < heights[shortest]) {
        shortest = column;
      }
    }
    columns[shortest].push({ profile, globalIndex });
    heights[shortest] += estimateCardHeight(
      profile.discover,
      columnWidth,
      maxImageHeight
    );
  });

  return columns;
}

/* Filters */

export enum EDiscoverFilterType {
  Featured = "featured",
  Query = "query",
  Type = "type",
  Gender = "gender",
}

export function getDefaultDiscoverFiltersState() {
  const localGenderPreference = JSON.parse(
    getLocalStorageValue("discoverFiltersGender") || "{}"
  );

  return {
    [EDiscoverFilterType.Featured]: {
      type: EDiscoverFilterType.Featured,
      value: true,
    },
    [EDiscoverFilterType.Query]: {
      type: EDiscoverFilterType.Query,
      value: "",
    },
    [EDiscoverFilterType.Type]: {
      type: EDiscoverFilterType.Type,
      value: null,
    },
    [EDiscoverFilterType.Gender]: {
      type: EDiscoverFilterType.Gender,
      value: {
        male: defined(localGenderPreference["male"])
          ? localGenderPreference["male"]
          : true,
        female: defined(localGenderPreference["female"])
          ? localGenderPreference["female"]
          : true,
        other: defined(localGenderPreference["other"])
          ? localGenderPreference["other"]
          : true,
      },
    },
  };
}

/* API */

const TO_API_KEY: Record<EDiscoverFilterType, string> = {
  [EDiscoverFilterType.Featured]: "featured",
  [EDiscoverFilterType.Query]: "q",
  [EDiscoverFilterType.Type]: "type",
  [EDiscoverFilterType.Gender]: "gender",
};

const TO_API_VALUE: Partial<
  Record<EDiscoverFilterType, Record<string, string | null>>
> = {
  [EDiscoverFilterType.Query]: {
    "": null,
  },
  [EDiscoverFilterType.Type]: {
    nearby: "near",
  },
  [EDiscoverFilterType.Gender]: {
    other: "trans",
  },
};

export function getProfilesUrl(config: IGetProfilesConfig) {
  const { isInitialRequest, offset, filtersSet } = config;
  // const initAmount = numberOfColumns(viewportWidth) * 4;
  const initAmount = 20;
  const limit = isInitialRequest ? initAmount : SUBSEQUENT_AMOUNT;
  let volumeQueries = `?offset=0&limit=${limit}`;
  if (defined(offset)) {
    volumeQueries = `?offset=${offset}&limit=${limit}`;
  }
  let url = `${API_ROOT}/v1/users/discover${volumeQueries}`;
  if (defined(config.onboardingCode)) {
    url += `&code_used=${config.onboardingCode}`;
  }
  if (defined(filtersSet)) {
    url = addFiltersToUrl(url, filtersSet);
  }
  return url;
}

export function addFiltersToUrl(
  url: string,
  filtersSet: IDictionary<IDiscoverFilter>
) {
  let urlWithFilters = url;

  Object.keys(filtersSet).forEach((key) => {
    const filterKey = key as EDiscoverFilterType;
    const filter = filtersSet[filterKey];
    const apiKey = TO_API_KEY[filterKey];
    let apiValue = filter.value;
    const valueMap = TO_API_VALUE[filterKey];

    if (
      valueMap &&
      typeof filter.value === "string" &&
      defined(valueMap[filter.value])
    ) {
      apiValue = valueMap[filter.value];
    }
    if (
      typeof filter.value === "object" &&
      filter.value !== null &&
      !Array.isArray(filter.value)
    ) {
      apiValue = Object.keys(filter.value)
        .filter((key) => filter.value[key])
        .map((key) => {
          if (valueMap && defined(valueMap[key])) {
            return valueMap[key];
          }
          return key;
        })
        .join(",");
    }
    if (defined(apiValue) && apiValue !== "") {
      urlWithFilters += `&${apiKey}=${apiValue}`;
    }
  });

  return urlWithFilters;
}

export function isNew(created_at: string) {
  const hoursDifference = timeDiff("now", created_at, EUnit.Hour);
  return hoursDifference < 72;
}

export const DISCOVER_CARDS_LIMIT = 10;
export const MIN_AMOUNT_OF_CARDS_BEFORE_FETCHING_MORE = 3;

export const isDiscoverCardsAccess = (
  user: IUserInfo,
  providerConfig: IProviderConfig
) =>
  user.type === EUserType.User &&
  user.gender === EGenderType.Male &&
  isFeature(EFeature.DiscoverCards, user, providerConfig);

export function getDiscoverLandingPath(
  user: IUserInfo,
  providerConfig: IProviderConfig,
  viewportWidth: number
) {
  if (!isDiscoverCardsAccess(user, providerConfig)) {
    return "/discover";
  }

  const savedView = getLocalStorageValue("discoverType");
  const prefersCards =
    savedView === "cards" ||
    (viewportWidth < FROM_TABLET && savedView !== "masonry");

  return prefersCards ? "/discover/cards" : "/discover";
}

// /discover/<profileId>; "cards" is the one reserved single-segment view.
export const isDiscoverProfilePath = (pathname: string) =>
  /^\/discover\/(?!cards\/?$)[^/]+\/?$/.test(pathname);
